import React, { useEffect, useMemo, useRef, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import BackArrowIcon from 'assets/actionPlan/backArrow.svg';
import DashboardIcon from 'assets/actionPlan/dashboard.svg';
import dayjs from 'dayjs';

import {
  getActions,
  getDashboardData,
  getDepartmentKPI,
  getDepartments,
  getEmployees,
} from '../../../features/actions/actionPlanActions';
import {
  selectActionsList,
  selectDashboardData,
  selectDepartmentKPI,
  selectEmployees,
} from '../../../features/actions/actionPlanSlice';
import { selectUserInfo } from '../../../features/auth/authSlice';
import { getActionListStatsConfig } from '../ActionPlanConstans';
import ActionFilter from '../actionFilter/ActionFilter';
import {
  ActionCardsWrapper,
  ActionListContainer,
  ActionListTitle,
  BackIcon,
  Container,
  SettingsIconBlock,
  SettingsImg,
  SettingsSubTitle,
  SettingsTitle,
  SettingsTitleBlock,
  SettingsTopbar,
  SmallBlock,
  SmallIconBox,
  SmallLabel,
  SmallStatCard,
  SmallValue,
  Tab,
  TabsContainer,
} from './ActionPlanDashboard.styles';
import DashboardTable from './table/DashboardTable';
import { canViewDepartmentDashboard, getUserRole } from './table/dashboardPermissions';
import { useActionPlanSearchParams } from './useSearchData';

const ActionPlanDashboard = () => {
  const dispatch = useDispatch();
  const { searchData, setSearchData } = useActionPlanSearchParams();
  const lastApiParamsRef = useRef(null);
  const prevDepartmentIdRef = useRef(null);

  const userInfo = useSelector(selectUserInfo);
  const allEmployees = useSelector(selectEmployees);
  const allActions = useSelector(selectActionsList) || [];
  const dashboardData = useSelector(selectDashboardData);
  const departmentKPI = useSelector(selectDepartmentKPI);
  const [estimationView, setEstimationView] = useState('QUANTITY');
  const userRole = getUserRole(userInfo);
  const canViewDepartment = canViewDepartmentDashboard(userRole);

  const filters = useMemo(
    () => ({
      period: searchData.period || 'DAILY',
      tabMode: searchData.tabMode || 'EMPLOYEE',
      estimationType: searchData.estimationType || 'QUANTITY',
      employeeId: searchData.employeeId,
      departmentId: searchData.departmentId,
      actionId: searchData.actionId,
      from: searchData.from,
      to: searchData.to,
    }),
    [JSON.stringify(searchData)]
  );

  const normalizeTableData = (data) => ({
    rows: Array.isArray(data?.rows) ? data.rows : [],
    summary: data?.summary || {},
  });

  const activeData = normalizeTableData(
    filters.tabMode === 'DEPARTMENT' ? departmentKPI : dashboardData
  );

  const dateRange = useMemo(() => {
    const today = dayjs();

    if (filters.from && filters.to) {
      return {
        from: dayjs(filters.from).format('YYYY-MM-DD'),
        to: dayjs(filters.to).format('YYYY-MM-DD'),
      };
    }

    return {
      from: today.startOf('month').format('YYYY-MM-DD'),
      to: today.endOf('month').format('YYYY-MM-DD'),
    };
  }, [filters.from, filters.to]);

  const apiParams = useMemo(() => {
    const base = {
      viewMode: filters.period,
      from: dateRange.from,
      to: dateRange.to,
    };

    if (filters.tabMode === 'EMPLOYEE') {
      if (filters.employeeId) {
        base.employeeId = filters.employeeId;
      }
      if (filters.departmentId) {
        base.departmentId = filters.departmentId;
      }
      if (filters.actionId) {
        base.actionId = filters.actionId;
      }
    }

    if (filters.tabMode === 'DEPARTMENT') {
      if (filters.departmentId) {
        base.departmentId = filters.departmentId;
      }
      if (filters.actionId) {
        base.actionId = filters.actionId;
      }
    }

    return base;
  }, [filters, dateRange]);

  const handleEstimationTypeChange = (type) => {
    setSearchData({
      estimationType: type,
    });
  };

  useEffect(() => {
    dispatch(getEmployees());
    dispatch(getDepartments());
  }, [dispatch]);

  useEffect(() => {
    if (
      filters.tabMode === 'DEPARTMENT' &&
      prevDepartmentIdRef.current !== null &&
      prevDepartmentIdRef.current !== filters.departmentId
    ) {
      setSearchData({ actionId: null });
    }
    prevDepartmentIdRef.current = filters.departmentId;
  }, [filters.departmentId, filters.tabMode, setSearchData]);

  useEffect(() => {
    if (filters.tabMode === 'DEPARTMENT') {
      if (filters.departmentId) {
        dispatch(getActions({ page: 0, size: 100, departmentId: filters.departmentId }));
      }
    } else {
      dispatch(getActions({ page: 0, size: 100 }));
    }
  }, [dispatch, filters.tabMode, filters.departmentId]);

  useEffect(() => {
    const baseValid = apiParams.viewMode && apiParams.from && apiParams.to;

    const paramsChanged =
      !lastApiParamsRef.current ||
      JSON.stringify(lastApiParamsRef.current) !==
        JSON.stringify({ ...apiParams, tabMode: filters.tabMode });

    if (!paramsChanged) {
      return;
    }

    if (filters.tabMode === 'DEPARTMENT') {
      if (baseValid && (filters.departmentId || filters.actionId)) {
        lastApiParamsRef.current = { ...apiParams, tabMode: filters.tabMode };
        dispatch(getDepartmentKPI(apiParams));
      }
    } else if (filters.tabMode === 'EMPLOYEE') {
      if (baseValid) {
        lastApiParamsRef.current = { ...apiParams, tabMode: filters.tabMode };
        dispatch(getDashboardData(apiParams));
      }
    }
  }, [dispatch, apiParams, filters.tabMode]);

  const totalPlanned =
    estimationView === 'FINANCIAL'
      ? activeData?.summary?.totalPlannedFinancial || 0
      : activeData?.summary?.totalPlannedQuantity || 0;

  const totalPresented =
    estimationView === 'FINANCIAL'
      ? activeData?.summary?.totalPresentedFinancial || 0
      : activeData?.summary?.totalPresentedQuantity || 0;

  const totalActual =
    estimationView === 'FINANCIAL'
      ? activeData?.summary?.totalActualFinancial || 0
      : activeData?.summary?.totalActualQuantity || 0;

  const actionListStats = getActionListStatsConfig({
    totalPlanned,
    totalPresented,
    totalActual,
  }).filter((stat) => {
    if (filters.tabMode === 'DEPARTMENT') {
      return stat.label !== 'TOTAL PRESENTED';
    }

    return true;
  });

  const handleFilterChange = (key, value) => {
    setSearchData({ [key]: value });
  };

  const handleTabChange = (mode) => {
    const updates = {
      tabMode: mode,
      employeeId: null,
      actionId: null,
      departmentId: null,
    };

    setSearchData(updates);
  };

  const handleClearAll = () => {
    setSearchData({
      employeeId: null,
      departmentId: null,
      actionId: null,
      from: null,
      to: null,
    });
  };

  const handleRefresh = () => {
    const baseValid = apiParams.viewMode && apiParams.from && apiParams.to;

    if (filters.tabMode === 'DEPARTMENT') {
      if (!baseValid || (!filters.departmentId && !filters.actionId)) return;
      dispatch(getDepartmentKPI(apiParams));
    } else {
      if (!baseValid) return;
      dispatch(getDashboardData(apiParams));
    }
  };

  return (
    <Container>
      <SettingsTopbar>
        {/* <BackIcon src={BackArrowIcon} /> */}
        <SettingsIconBlock>
          <SettingsImg src={DashboardIcon} />
        </SettingsIconBlock>
        <SettingsTitleBlock>
          <SettingsTitle>Dashboard</SettingsTitle>
          <SettingsSubTitle>
            Overview of KPI performance and action management
          </SettingsSubTitle>
        </SettingsTitleBlock>
      </SettingsTopbar>
      {canViewDepartment && (
        <TabsContainer>
          <Tab
            $isActive={filters.tabMode === 'EMPLOYEE'}
            onClick={() => handleTabChange('EMPLOYEE')}
          >
            Employee
          </Tab>
          <Tab
            $isActive={filters.tabMode === 'DEPARTMENT'}
            onClick={() => handleTabChange('DEPARTMENT')}
          >
            Department
          </Tab>
        </TabsContainer>
      )}
      <ActionFilter
        filters={filters}
        employees={allEmployees}
        actions={allActions}
        onFilterChange={handleFilterChange}
        onClearAll={handleClearAll}
      />
      <ActionListTitle>Action List</ActionListTitle>
      <TabsContainer>
        <Tab
          $isActive={estimationView === 'FINANCIAL'}
          onClick={() => setEstimationView('FINANCIAL')}
        >
          Financial
        </Tab>

        <Tab
          $isActive={estimationView === 'QUANTITY'}
          onClick={() => setEstimationView('QUANTITY')}
        >
          Quantity
        </Tab>
      </TabsContainer>
      <ActionListContainer>
        <ActionCardsWrapper>
          {actionListStats.map((stat, index) => (
            <SmallStatCard key={index} bgColor={stat.bgColor} borderColor={stat.borderColor}>
              <SmallIconBox iconBg={stat.iconBg}>{stat.icon && <stat.icon />}</SmallIconBox>
              <SmallBlock>
                <SmallLabel color={stat.labelColor}>{stat.label}</SmallLabel>
                <SmallValue color={stat.valueColor}>{stat.value}</SmallValue>
              </SmallBlock>
            </SmallStatCard>
          ))}
        </ActionCardsWrapper>
      </ActionListContainer>
      <DashboardTable
        data={normalizeTableData(activeData)}
        viewMode={filters.period}
        onRefresh={handleRefresh}
        searchParams={apiParams}
        filters={filters}
      />
    </Container>
  );
};

export default ActionPlanDashboard;
